import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Award, Layers, Clock, CheckCircle2, ChevronRight, Calendar, FileText } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";

const STATUS_BADGE = {
  enrolled: { label: "Inscrit", color: "bg-slate-500/10 text-slate-400 border-slate-500/30", icon: Clock },
  en_validation: { label: "En cours", color: "bg-blue-500/10 text-blue-400 border-blue-500/30", icon: Clock },
  certified: { label: "Certifie", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  partial: { label: "Validation partielle", color: "bg-amber-500/10 text-amber-400 border-amber-500/30", icon: CheckCircle2 },
  refused: { label: "Refuse", color: "bg-red-500/10 text-red-400 border-red-500/30", icon: Clock },
  abandoned: { label: "Abandonne", color: "bg-slate-700/30 text-slate-500 border-slate-700/40", icon: Clock },
};

export default function MesParcoursCertifiantsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/candidate/me/parcours");
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6" data-testid="mes-parcours-page">
      <PageHeader
        icon={<Award />}
        title="Mes parcours certifiants"
        subtitle="Le suivi de vos parcours certifiants RS / RNCP : progression par bloc, jury, preuves deposees, parchemin."
      />

      {loading ? <ListSkeleton items={3} /> : items.length === 0 ? (
        <div className="text-center py-20 bg-[#14101c] rounded-xl border border-white/10">
          <Award className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 mb-2">Aucun parcours certifiant en cours</p>
          <p className="text-xs text-slate-500">Inscrivez-vous a une certification depuis le catalogue ou via votre organisme.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="parcours-list">
          {items.map(c => {
            const status = STATUS_BADGE[c.cert_status] || STATUS_BADGE.enrolled;
            const StatusIcon = status.icon;
            const cert = c.certification || {};
            const valides = (c.parcours_blocs || []).filter(p => p.status === "valide").length;
            const total = (c.parcours_blocs || []).length || 1;
            const progress = Math.round((valides / total) * 100);
            return (
              <Link
                key={c.candidate_id}
                to={`/mon-parcours-certifiant/${c.candidate_id}`}
                data-testid={`parcours-row-${c.candidate_id}`}
                className="block bg-[#14101c] border border-white/10 rounded-xl p-5 hover:border-[#f7c935]/30 transition-all hover:-translate-y-0.5"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={cert.type === "RNCP" ? "bg-purple-500/10 text-purple-400 border-purple-500/30" : "bg-blue-500/10 text-blue-400 border-blue-500/30"}>{cert.type}</Badge>
                      <span className="text-xs text-[#f7c935] font-mono">{cert.code}</span>
                      <Badge className={status.color}><StatusIcon className="w-3 h-3 mr-1" /> {status.label}</Badge>
                    </div>
                    <h3 className="text-base font-semibold text-white mb-1">{cert.title}</h3>
                    {cert.niveau && <p className="text-xs text-slate-500">{cert.niveau}</p>}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-xs text-slate-500 flex items-center justify-end gap-1"><Layers className="w-3 h-3" /> Blocs</div>
                      <div className="text-xl font-bold text-white">{valides}/{total}</div>
                    </div>
                    {c.jury && (
                      <div className="text-right">
                        <div className="text-xs text-slate-500 flex items-center justify-end gap-1"><Calendar className="w-3 h-3" /> Jury</div>
                        <div className="text-sm font-semibold text-white">{c.jury.convocation_date}</div>
                      </div>
                    )}
                    {(c.proofs_count || 0) > 0 && (
                      <div className="text-right">
                        <div className="text-xs text-slate-500 flex items-center justify-end gap-1"><FileText className="w-3 h-3" /> Preuves</div>
                        <div className="text-sm font-semibold text-white">{c.proofs_count}</div>
                      </div>
                    )}
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </div>
                <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#f7c935] to-emerald-400 transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
